import { Head, Link, usePage } from "@inertiajs/react"
import { useState } from "react"
import {
  Mic,
  CheckCircle2,
  GraduationCap,
  Gift,
  Lightbulb,
  BadgePercent,
  MessageCircle,
  Check,
  ChevronDown,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { signInPath, signUpPath } from "@/routes"
import { usePrefetchLink } from "@/hooks/use-prefetch-link"
import { APP_URL, FAQ_DATA } from "@/constants"
import MarketingLayout from "@/layouts/marketing-layout"

export default function Welcome() {
  const page = usePage()
  const { auth } = page.props
  const signInPrefetch = usePrefetchLink(signInPath())
  const signUpPrefetch = usePrefetchLink(signUpPath())
  const dashboardPrefetch = usePrefetchLink("/dashboard")
  // const conversationsPrefetch = usePrefetchLink("/conversations")

  return (
    <MarketingLayout>
      <Head title="ThaiCopilot | Speak Thai in Minutes, Not Months">
        {/* Essential Meta Tags */}
        <meta
          name="description"
          content="Learn Thai to speak Thai in minutes, not months with ThaiCopilot's AI-powered platform. Use the platform when you need to speak Thai in real life situations and practice 10-15 minutes daily with flashcards, to learn Thai in minutes, not months. Perfect for expats and travelers."
        />
        <meta
          name="keywords"
          content="learn Thai, learn Thai with AI, learn to speak Thai efficiently, Thai language learning, AI Thai tutor, learn Thai online, Thai conversation practice, learn Thai fast, learn Thai for expats, learn Thai for travelers, speak Thai efficiently, Thai learning app, learn Thai GenAI, learn Thai with ChatGPT"
        />
        <meta name="author" content="ThaiCopilot" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={
            typeof window !== "undefined" ? window.location.href : APP_URL
          }
        />
        <meta
          property="og:title"
          content="ThaiCopilot | Speak Thai in Minutes, Not Months"
        />
        <meta
          property="og:description"
          content="Learn Thai to speak Thai in minutes, not months with ThaiCopilot's AI-powered platform. Use the platform when you need to speak Thai in real life situations and practice 10-15 minutes daily with flashcards, to learn Thai in minutes, not months. Perfect for expats and travelers."
        />
        <meta property="og:site_name" content="ThaiCopilot" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={`${APP_URL}/ThaiCopilotLogo.png`} />
        <meta property="og:image:width" content="1263" />
        <meta property="og:image:height" content="1146" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="ThaiCopilot | Speak Thai in Minutes, Not Months"
        />
        <meta
          name="twitter:description"
          content="Learn Thai to speak Thai in minutes, not months with ThaiCopilot's AI-powered platform. Use the platform when you need to speak Thai in real life situations and practice 10-15 minutes daily with flashcards, to learn Thai in minutes, not months. Perfect for expats and travelers."
        />
        <meta name="twitter:image" content={`${APP_URL}/ThaiCopilotLogo.png`} />

        {/* Additional SEO */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="language" content="English" />
        <meta name="geo.region" content="TH" />
        <meta name="geo.placename" content="Thailand" />
        <link
          rel="canonical"
          href={typeof window !== "undefined" ? window.location.href : APP_URL}
        />

      </Head>

      {/* Hero Section */}
      <HeroSection />

      {/* How It Works */}
      <HowItWorksSection />

      {/* Who This Is For */}
      <WhoThisIsForSection />

      {/* Alpha Perks */}
      <AlphaPerksSection />

      {/* FAQ */}
      <FAQSection />

      {/* Final CTA */}
      <FinalCTASection />
    </MarketingLayout>
  )
}

function HeroSection() {
  const page = usePage()
  const { auth } = page.props
  const signUpPrefetch = usePrefetchLink(signUpPath())
  const dashboardPrefetch = usePrefetchLink("/dashboard")

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden">
      {/* Enhanced Background gradient with subtle pattern */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900" />
      <div
        className="absolute inset-0 -z-10 opacity-[0.02] dark:opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgb(99 102 241) 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="container mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Capsule Badge */}
          <div className="mb-6 flex justify-center sm:mb-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-700 backdrop-blur-sm transition-transform duration-200 hover:scale-105 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-500"></span>
              </span>
              AI-Powered + Community-Driven Learning
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="mb-6 text-4xl font-bold text-slate-900 sm:text-5xl md:text-6xl lg:text-7xl dark:text-white">
            Learn to Speak Thai{" "}
            <span className="from-primary to-accent relative inline-block bg-gradient-to-r bg-clip-text text-transparent">
              Efficiently!
              <svg
                className="text-primary absolute -bottom-2 left-0 h-3 w-full opacity-30"
                viewBox="0 0 200 12"
                preserveAspectRatio="none"
              >
                <path
                  d="M0,7 Q50,0 100,7 T200,7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="mx-auto mb-10 max-w-4xl text-lg leading-relaxed font-medium text-slate-700 sm:mb-12 sm:text-xl md:text-2xl dark:text-slate-200">
            AI conversations, flashcards, growing community.
            <br className="hidden sm:block" />
            Start in 30 seconds — it's free.
          </p>

          {/* CTA Buttons */}
          <div className="mb-12 flex flex-col items-center justify-center gap-4 sm:mb-16 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="w-full transition-transform hover:scale-105 sm:w-auto"
            >
              <Link
                href={auth.user ? "/dashboard" : signUpPath()}
                {...(auth.user ? dashboardPrefetch : signUpPrefetch)}
              >
                {auth.user ? "Go to Dashboard" : "Start Your Journey Free"}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="w-full transition-transform hover:scale-105 sm:w-auto"
            >
              <a href="#how-it-works">Learn More</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Gradient transition to next section */}
      <div className="absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent to-slate-50 dark:to-slate-900" />
    </section>
  )
}

function HowItWorksSection() {
  const steps = [
    {
      number: "01",
      icon: Lightbulb,
      title: "Prepare What to Say",
      description:
        "English in → Thai out. Translation, audio, romanization. Ready in seconds.",
      colors: {
        badge:
          "from-emerald-500 to-teal-600 dark:from-emerald-400 dark:to-teal-500",
        iconBg: "bg-emerald-100 dark:bg-emerald-950/50",
        icon: "text-emerald-600 dark:text-emerald-400",
        border: "border-emerald-200 dark:border-emerald-800/50",
        shadow: "shadow-emerald-500/30",
        hoverShadow:
          "hover:shadow-emerald-500/20 dark:hover:shadow-emerald-500/10",
        glow: "hover:border-emerald-300 dark:hover:border-emerald-700/50",
      },
    },
    {
      number: "02",
      icon: MessageCircle,
      title: "Speak Thai Confidently",
      description:
        "Use it at markets, restaurants, with friends. Real conversations.",
      colors: {
        badge:
          "from-indigo-500 to-blue-600 dark:from-indigo-400 dark:to-blue-500",
        iconBg: "bg-indigo-100 dark:bg-indigo-950/50",
        icon: "text-indigo-600 dark:text-indigo-400",
        border: "border-indigo-200 dark:border-indigo-800/50",
        shadow: "shadow-indigo-500/30",
        hoverShadow:
          "hover:shadow-indigo-500/20 dark:hover:shadow-indigo-500/10",
        glow: "hover:border-indigo-300 dark:hover:border-indigo-700/50",
      },
    },
    {
      number: "03",
      icon: GraduationCap,
      title: "Never Forget",
      description:
        "Flashcards from your life. 10-15 minutes daily. Permanent progress.",
      colors: {
        badge:
          "from-violet-500 to-purple-600 dark:from-violet-400 dark:to-purple-500",
        iconBg: "bg-violet-100 dark:bg-violet-950/50",
        icon: "text-violet-600 dark:text-violet-400",
        border: "border-violet-200 dark:border-violet-800/50",
        shadow: "shadow-violet-500/30",
        hoverShadow:
          "hover:shadow-violet-500/20 dark:hover:shadow-violet-500/10",
        glow: "hover:border-violet-300 dark:hover:border-violet-700/50",
      },
    },
  ]

  return (
    <section
      id="how-it-works"
      className="relative flex min-h-screen items-center bg-slate-50 dark:bg-slate-900"
    >
      <div className="container mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl dark:text-white">
            How to Learn Thai with AI
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-300">
            Three simple steps to learn to speak Thai quickly through everyday
            conversations
          </p>
          <div className="mt-4">
            <Link
              href="/how-it-works"
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Watch complete walkthrough
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`group relative border-2 bg-white dark:bg-slate-900 ${step.colors.border} rounded-2xl p-8 hover:shadow-xl ${step.colors.hoverShadow} ${step.colors.glow} transition-all duration-300 hover:-translate-y-2`}
            >
              {/* Decorative gradient overlay on hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${step.colors.badge} rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-5`}
              />

              {/* Step Number Badge */}
              <div
                className={`absolute -top-4 -right-4 h-14 w-14 rounded-2xl bg-gradient-to-br ${step.colors.badge} flex items-center justify-center text-lg font-bold text-white shadow-xl ${step.colors.shadow} rotate-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12`}
              >
                {step.number}
              </div>

              {/* Icon with matching color */}
              <div
                className={`relative h-16 w-16 rounded-2xl ${step.colors.iconBg} mb-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}
              >
                <step.icon className={`h-8 w-8 ${step.colors.icon}`} />
              </div>

              <h3 className="relative mb-3 text-xl font-semibold text-slate-900 dark:text-white">
                {step.title}
              </h3>
              <p className="relative text-base leading-relaxed text-slate-600 dark:text-slate-300">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Gradient transition to next section */}
      <div className="absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent to-white dark:to-slate-950" />
    </section>
  )
}

function WhoThisIsForSection() {
  const items = [
    "Already try to speak Thai but forget everything by the next day",
    "Have Thai friends but no structured way to improve from conversations",
    "Want to learn from real situations",
    "Are willing to test early software and give honest feedback",
    "Are tired of apps that teach you words you'll never use",
  ]

  return (
    <section className="relative flex min-h-screen items-center bg-white dark:bg-slate-950">
      <div className="container mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl dark:text-white">
            Learn Thai Fast — Built for Expats in Thailand
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300">
            Perfect for anyone wanting to learn Thai quickly. This alpha is
            ideal if you:
          </p>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:hover:shadow-indigo-500/10"
            >
              <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 dark:bg-indigo-500">
                <Check className="h-4 w-4 text-white" strokeWidth={3} />
              </div>
              <p className="text-base leading-relaxed text-slate-700 md:text-lg dark:text-slate-200">
                {item}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Gradient transition to next section */}
      <div className="absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent to-slate-50 dark:to-slate-900" />
    </section>
  )
}

function AlphaPerksSection() {
  const perks = [
    {
      icon: Gift,
      title: "100% Free. No Catch.",
      description:
        "Full access to every feature. No credit card required. No trial limits.",
    },
    {
      icon: Lightbulb,
      title: "Shape the Product",
      description: "Your feedback directly influences what we build",
    },
    {
      icon: BadgePercent,
      title: "Paid Plans? Not Yet.",
      description:
        "We're focused on building something valuable first. Pricing is a future consideration.",
    },
    {
      icon: MessageCircle,
      title: "Direct Founder Access",
      description: "Talk directly to the founder. No support tickets.",
    },
  ]

  return (
    <section className="relative flex min-h-screen items-center bg-slate-50 dark:bg-slate-900">
      <div className="container mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl dark:text-white">
            Learn Thai with AI — Alpha Users Perks
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-300">
            Be among the first 50 people to learn Thai with AI and shape the
            future of Thai language learning
          </p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2">
          {perks.map((perk, index) => (
            <div
              key={index}
              className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:from-slate-900 dark:to-slate-950 dark:hover:shadow-indigo-500/10"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-indigo-600 dark:bg-indigo-500">
                <perk.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="mb-3 text-xl font-semibold text-slate-900 dark:text-white">
                {perk.title}
              </h3>
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300">
                {perk.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Gradient transition to next section */}
      <div className="absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent to-white dark:to-slate-950" />
    </section>
  )
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const faqs = FAQ_DATA

  return (
    <section className="relative flex min-h-screen items-center bg-white dark:bg-slate-950">
      <div className="container mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl dark:text-white">
            Learn Thai with AI — FAQ
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300">
            Everything you need to know about learning Thai efficiently with
            ThaiCopilot
          </p>
        </div>

        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm md:p-8 dark:border-slate-800 dark:bg-slate-950">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border-b border-slate-200 last:border-0 dark:border-slate-800"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="group flex w-full items-center justify-between py-5 text-left"
              >
                <span className="pr-8 text-lg font-medium text-slate-900 dark:text-white">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-indigo-600 transition-transform duration-200 dark:text-indigo-400 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  openIndex === index ? "max-h-96 pb-5" : "max-h-0"
                }`}
              >
                <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gradient transition to dark CTA section */}
      <div className="absolute right-0 bottom-0 left-0 h-40 bg-gradient-to-b from-transparent to-slate-900 dark:to-black" />
    </section>
  )
}

function FinalCTASection() {
  const signUpPrefetch = usePrefetchLink(signUpPath())

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden bg-slate-900 dark:bg-black">
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "4rem 4rem",
        }}
      />

      <div className="relative container mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
        <h2 className="mb-6 text-4xl leading-tight font-bold text-white sm:text-5xl">
          Ready to Learn Thai with AI?
        </h2>
        <p className="mb-12 text-xl text-slate-300">
          Join an early group of expats learning to speak Thai efficiently with
          ThaiCopilot.
        </p>

        {/* Sign Up Button */}
        <div className="flex justify-center">
          <Button
            asChild
            size="lg"
            className="transition-transform hover:scale-105"
          >
            <Link href={signUpPath()} {...signUpPrefetch}>
              Get Started Now
            </Link>
          </Button>
        </div>
      </div>

      {/* Gradient transition to footer */}
      <div className="absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent to-slate-50 dark:to-slate-900" />
    </section>
  )
}
